import {PaymentInstructionStatus} from '@sollapay/enums';
import {toast, ToastCard} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {FC} from 'react';

export type PaymentInstructionStatusToastProps = {
  toastId: string | number;
  status:
    | PaymentInstructionStatus.AWAITING_FUNDS
    | PaymentInstructionStatus.FUNDS_IN_TRANSIT
    | PaymentInstructionStatus.FAILED
    | PaymentInstructionStatus.REJECTED;
  amount?: string;
  isManual?: boolean;
  onNavigate: () => void;
};

function getStatusKey(
  status: PaymentInstructionStatusToastProps['status'],
  isManual?: boolean
): string {
  if (status === PaymentInstructionStatus.AWAITING_FUNDS) {
    return isManual ? 'awaitingFundsManual' : 'awaitingFunds';
  }
  if (status === PaymentInstructionStatus.FUNDS_IN_TRANSIT) return 'fundsInTransit';
  if (status === PaymentInstructionStatus.REJECTED) return 'rejected';
  return 'failed';
}

export const PaymentInstructionStatusToast: FC<PaymentInstructionStatusToastProps> = ({
  toastId,
  status,
  amount,
  isManual,
  onNavigate
}) => {
  const {t} = useTranslation('payments');
  const key = getStatusKey(status, isManual);
  const isError =
    status === PaymentInstructionStatus.FAILED || status === PaymentInstructionStatus.REJECTED;

  return (
    <ToastCard
      iconName={isError ? 'sollapay:close' : 'sollapay:check'}
      title={t(`paymentInstructionStatusToast.${key}.title`)}
      description={t(`paymentInstructionStatusToast.${key}.description`, {amount})}
      action={{label: t('paymentInstructionStatusToast.actionLabel'), onClick: onNavigate}}
      onClose={() => toast.dismiss(toastId)}
      closeLabel={t('paymentInstructionStatusToast.close')}
    />
  );
};

export function showPaymentInstructionStatusToast(
  status: PaymentInstructionStatusToastProps['status'],
  onNavigate: () => void,
  options?: {amount?: string; isManual?: boolean}
): void {
  const isTerminal =
    status === PaymentInstructionStatus.FAILED || status === PaymentInstructionStatus.REJECTED;
  toast.custom(
    id => (
      <PaymentInstructionStatusToast
        toastId={id}
        status={status}
        amount={options?.amount}
        isManual={options?.isManual}
        onNavigate={onNavigate}
      />
    ),
    {
      duration: isTerminal ? 30_000 : 10_000
    }
  );
}
